import React from 'react';
import DateTime, { Parser, ITimeZone } from '@dealroadshow/uikit-date-time';
import { format, isSameDay, toDate, isValid } from 'date-fns';
import { Link } from 'react-router-dom';
import memoize from '@framework/data-helpers/memoize';
import Status from '@/condor/ui/common/Layout/Status';
import Tooltip from '@dealroadshow/uikit-tooltip';

import formatNumber from '@framework/data-helpers/formatters/formatNumber';
import utcToZonedDate from '@framework/date-helpers/utcToZonedDate';
import {
  SHORT_MONTH_NAME_DAY_FULL_YEAR,
  MONTH_DAY_YEAR_FORMAT,
  TIME_FORMAT_12,
  YEAR_MONTH_DAY_DASH_FORMAT,
} from '@dealroadshow/uikit-date-time/dateFormats';

import alignStyles from '@dealroadshow/uikit-styles/helpers/align.module.scss';
import IconMinus from '@dealroadshow/uikit-icon/IconMinus';
import IconPlus from '@dealroadshow/uikit-icon/IconPlus';
import { IconComponentType } from '@dealroadshow/uikit-icon';
import { DEFAULT_TIME_ZONE } from '@framework/timezone/constants';

/**
 * @param {Object} cellProps
 * @return {String}
 */
export function commonCellCallback(cellProps) {
  if (!cellProps.row[cellProps.cellName]) {
    return '-';
  }
  return cellProps.row[cellProps.cellName];
}

interface IopenProfileCellWrapperParams {
  tooltipTitle?: string,
  href: string,
  className?: string,
  children: React.ReactNode,
}

export function openProfileCellWrapper({
  tooltipTitle,
  href,
  className,
  children,
}: IopenProfileCellWrapperParams) {
  return (
    <Tooltip content={ tooltipTitle || 'Open Profile' }>
      <Link
        to={ href }
        className={ className }
      >
        { children }
      </Link>
    </Tooltip>
  );
}

/**
 * @param {Object} status
 */
export function statusCellWrapper(status) {
  return (
    <Status
      className={ alignStyles.alignCenter }
      status={ status }
    />
  );
}

const dateToMonthDayYearOrTime = (
  {
    timeZone = DEFAULT_TIME_ZONE.timeZone,
    dateTime,
    formatString,
  }:
  { timeZone?: string, dateTime: Date, formatString?: string },
) => {
  const time = utcToZonedDate(timeZone, new Date(dateTime));

  if (formatString) {
    return format(time, formatString);
  }

  const isToday = isSameDay(time, utcToZonedDate(timeZone));
  if (isToday) {
    return `${ format(time, TIME_FORMAT_12) } ET`;
  }

  return format(time, MONTH_DAY_YEAR_FORMAT);
};

export const getCellDisplayDateFromNumber = (date, formatString?: string) => {
  if (date) {
    const dateValue = new Date(date * 1000);

    return dateToMonthDayYearOrTime({ dateTime: dateValue, formatString });
  }

  return '-';
};

export const getCellDisplayDateFromString = (date, formatString?: string) => {
  if (date) {
    const dateValue = toDate(new Date(date));

    return dateToMonthDayYearOrTime({ dateTime: dateValue, formatString });
  }

  return '-';
};

interface IFormatDateOptions {
  parseFormat?: string,
  timeZone?: ITimeZone,
  formatString?: string,
}

export const getAndFormatDateFromString = (
  date: string | null | undefined,
  options: IFormatDateOptions = {},
): string => {
  const {
    parseFormat = YEAR_MONTH_DAY_DASH_FORMAT,
    timeZone = DEFAULT_TIME_ZONE,
    formatString = SHORT_MONTH_NAME_DAY_FULL_YEAR,
  } = options;

  const dateObject = toDate(new Date(date));

  if (!date || !isValid(dateObject)) {
    return '-';
  }

  const dt = new DateTime(date, {
      timeZone,
      parsers: [
        new Parser({
          format: parseFormat,
          timeZone,
        }),
      ],
    },
  );

  return dt.format(formatString);
};

/**
 * @param {Object} item
 */
export const createOption = (item) => ({
  label: item.name,
  value: item.id,
});

/**
 * @param {Array} options
 * @param {Function} callBack
 */
export const mapOptions = (options, callBack = createOption) => options.map(callBack);

export const mapOptionsMemoized = memoize(mapOptions);

export const panelArrow = (arrowText: string = '', Icon: IconComponentType) => (
  <div>
    { (typeof arrowText === 'string') && (
      <span className="panelGroupHeaderArrowText">{ arrowText }</span>
    ) }
    <span className="panelGroupHeaderArrowIcon">
      <Icon />
    </span>
  </div>
);

export const panelArrowCollapsed = (arrowText: string = '') => panelArrow(arrowText, IconPlus);

/**
 * @param arrowText
 */
export const panelArrowExpanded = (arrowText = '') => panelArrow(arrowText, IconMinus);

/**
 @param {object} cellProps
 @return {String}
 */
export const getFormattingNumberCellCallback = (cellProps) => {
  if (cellProps.cellData) {
    return formatNumber(cellProps.cellData);
  }

  return '-';
};
